import { inject, Injectable } from "@angular/core";

import { Observable, tap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { environment } from "@environments/environment";
import {
  ActivatePlanResponse,
  ApiRequestConfig,
  PlanRecord,
  PlansResponse,
  PlanUpdateRecord,
  PostConfig,
  RequestConfig,
  RequestState,
} from "@shared/models";
import { CacheId } from "@shared/enums";
import { ApiCacheService } from "@shared/services/util/api-cache.service";
import { ApiService } from "@shared/services/util/api.service";

@Injectable({
  providedIn: "root",
})
export class PlanApiService {
  apiCacheService = inject(ApiCacheService);
  apiService = inject(ApiService);

  private readonly _baseUrl = environment.imaxApiUrl;

  private _plansRequestConfig: RequestConfig = {
    url: `${this._baseUrl}/GetPlan`,
    cacheId: CacheId.ActivePlan,
    cacheValidMinutes: 5,
    forceRefresh: false,
    arrayKey: "plans",
  };

  /**
   * Uses valid cached API plans response or fetches a new one.
   * @param {boolean} force - Forces a new API call.
   * @param {number} plan - The plan to fetch.
   * @returns {Observable<RequestState<PlansResponse>>} Observable of {@link RequestState}<{@link PlansResponse}>.
   */
  getPlan(force: boolean, plan: number): Observable<RequestState<PlansResponse>> {
    this._plansRequestConfig.forceRefresh = force;
    this._plansRequestConfig.cacheId = plan === 0 ? CacheId.ActivePlan : CacheId.StandbyPlan;
    this._plansRequestConfig.url = `${this._baseUrl}/GetPlan?plan=${plan}`;
    return this.apiCacheService.request<PlansResponse>(this._plansRequestConfig);
  }

  updatePlanRecords(planRecords: PlanUpdateRecord[]): Observable<PlanRecord[] | null> {
    const requestConfig: ApiRequestConfig = {
      url: `${this._baseUrl}/UpdatePlanRecords`,
      body: planRecords,
    };
    return this.apiService.post<PlanRecord[]>(requestConfig);
  }

  activateStandbyPlan(): Observable<ActivatePlanResponse | null> {
    const requestConfig: ApiRequestConfig = {
      url: `${this._baseUrl}/ActivateStandbyPlan`,
    };
    return this.apiService.get<ActivatePlanResponse>(requestConfig);
  }
}
